<script setup>
import {
  ref,
  onMounted,
  onBeforeUnmount,
  computed,
  watch,
  nextTick,
} from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import TicketFiltersBar from '../components/TicketFiltersBar.vue';
import ColumnConfiguration from '../components/ColumnConfiguration.vue';
import FilterConfiguration from '../components/FilterConfiguration.vue';

defineEmits(['createTicket']);
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const tickets = computed(() => store.getters['tickets/getTickets'] || []);
const ticketFilters = computed(
  () => store.getters['tickets/getFiltersByView']('list') || {}
);
const uiFlags = computed(
  () => store.getters['tickets/getUIFlags'] || { isFetching: false }
);

const statuses = computed(() => store.getters['tickets/getStatuses'] || []);
const priorities = computed(() => store.getters['tickets/getPriorities'] || []);
const users = computed(() => store.getters['agents/getAgents'] || []);
const teams = computed(() => store.getters['teams/getMyTeams'] || []);
const contacts = computed(() => store.getters['contacts/getContacts'] || []);
const businessAccounts = computed(
  () => store.getters['businessAccounts/getBusinessAccountsList'] || []
);
const ticketAttributes = computed(
  () => store.getters['attributes/getTicketAttributes'] || []
);

const filterListOptions = computed(() => [
  {
    key: 'status',
    label: t('TICKETS.STATUS'),
    icon: '📊',
    color: 'blue',
    valueKey: 'name',
    labelKey: 'name',
  },
  {
    key: 'priority',
    label: t('TICKETS.PRIORITY'),
    icon: '⚡',
    color: 'green',
    valueKey: 'name',
    labelKey: 'name',
  },
  {
    key: 'contact_id',
    label: t('TICKETS.CONTACT'),
    icon: '📧',
    color: 'pink',
    valueKey: 'id',
    labelKey: 'name',
  },
  {
    key: 'team_id',
    label: t('TICKETS.TEAM'),
    icon: '👥',
    color: 'purple',
    valueKey: 'id',
    labelKey: 'name',
  },
  {
    key: 'assignee_id',
    label: t('TICKETS.ASSIGNEE'),
    icon: '👤',
    color: 'orange',
    valueKey: 'id',
    labelKey: 'name',
  },
  {
    key: 'business_account_id',
    label: t('TICKETS.BUSINESS_ACCOUNT'),
    icon: '🏢',
    color: 'teal',
    valueKey: 'id',
    labelKey: 'name',
  },
  {
    key: 'custom_attributes',
    label: t('TICKETS.CUSTOM_ATTRIBUTES'),
    icon: '🏷️',
    color: 'indigo',
    valueKey: 'id',
    labelKey: 'name',
  },
]);

const columnOptions = computed(() => [
  { key: 'title', label: t('TICKETS.TITLE') },
  { key: 'description', label: t('TICKETS.DESCRIPTION') },
  { key: 'status', label: t('TICKETS.STATUS') },
  { key: 'priority', label: t('TICKETS.PRIORITY') },
  { key: 'contact', label: t('TICKETS.CONTACT') },
  { key: 'team', label: t('TICKETS.TEAM') },
  { key: 'assignee', label: t('TICKETS.ASSIGNEE') },
  { key: 'business_account', label: t('TICKETS.BUSINESS_ACCOUNT') },
]);

// Load configuration from localStorage or use defaults
const STORAGE_KEY_COLUMNS = 'ticket_list_visible_columns';
const STORAGE_KEY_FILTERS = 'ticket_list_visible_filters';
const STORAGE_KEY_FILTER_VALUES = 'ticket_list_filter_values';
const STORAGE_KEY_SEARCH_QUERY = 'ticket_list_search_query';

const defaultVisibleColumns = computed(() =>
  columnOptions.value.map(col => col.key)
);
const defaultVisibleFilters = computed(() =>
  filterListOptions.value.map(filter => filter.key)
);

const getStoredColumns = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_COLUMNS);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Ensure at least one column is visible
      return parsed.length > 0 ? parsed : defaultVisibleColumns.value;
    }
  } catch (e) {
    // Error loading stored columns - silently fail
  }
  return defaultVisibleColumns.value;
};

const getStoredFilters = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_FILTERS);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Error loading stored filters - silently fail
  }
  return defaultVisibleFilters.value;
};

const visibleColumns = ref(getStoredColumns());
const visibleFilters = ref(getStoredFilters());

// Load filter values and search query from localStorage
const loadStoredFilterValues = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_FILTER_VALUES);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && Object.keys(parsed).length > 0) {
        store.dispatch('tickets/setFilters', { view: 'list', filters: parsed });
      }
    }
  } catch (e) {
    // Error loading stored filter values - silently fail
  }
};

const loadStoredSearchQuery = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_SEARCH_QUERY);
    if (stored) {
      return stored;
    }
  } catch (e) {
    // Error loading stored search query - silently fail
  }
  return '';
};

const error = ref(null);
const currentPage = ref(1);
const perPage = ref(25);
const searchQuery = ref(loadStoredSearchQuery());
const searchTimeout = ref(null);
const searchInputRef = ref(null);
const ticketFiltersRef = ref(null);
const saveFiltersTimeout = ref(null);
const isBootstrapping = ref(true);

// Watch for changes and persist to localStorage
watch(
  visibleColumns,
  newColumns => {
    try {
      localStorage.setItem(STORAGE_KEY_COLUMNS, JSON.stringify(newColumns));
    } catch (e) {
      // Error saving columns to localStorage - silently fail
    }
  },
  { deep: true }
);

watch(
  visibleFilters,
  newFilters => {
    try {
      localStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify(newFilters));
    } catch (e) {
      // Error saving filters to localStorage - silently fail
    }
  },
  { deep: true }
);

// Watch filter values and save to localStorage
watch(
  () => ticketFilters.value,
  newFilters => {
    try {
      // Only save if filters have values
      const hasFilters = Object.keys(newFilters).some(key => {
        if (key === 'custom_attributes') {
          return Object.keys(newFilters[key] || {}).length > 0;
        }
        return Array.isArray(newFilters[key]) && newFilters[key].length > 0;
      });

      if (hasFilters) {
        localStorage.setItem(
          STORAGE_KEY_FILTER_VALUES,
          JSON.stringify(newFilters)
        );
      } else {
        // Clear if no filters
        localStorage.removeItem(STORAGE_KEY_FILTER_VALUES);
      }
    } catch (e) {
      // Error saving filter values to localStorage - silently fail
    }
  },
  { deep: true }
);

// Computed for all available columns including custom attributes
const allColumnOptions = computed(() => {
  const baseColumns = [...columnOptions.value];
  const customAttrColumns = ticketAttributes.value.map(attr => ({
    key: `custom_attr_${attr.id}`,
    label: attr.attributeDisplayName,
  }));
  return [...baseColumns, ...customAttrColumns];
});

// Computed for visible custom attribute columns
const visibleCustomAttributeColumns = computed(() => {
  return ticketAttributes.value.filter(attr =>
    visibleColumns.value.includes(`custom_attr_${attr.id}`)
  );
});

const filterData = computed(() => ({
  status: statuses.value || [],
  priority: priorities.value || [],
  contact_id: contacts.value || [],
  team_id: teams.value || [],
  assignee_id: users.value || [],
  business_account_id: businessAccounts.value || [],
  custom_attributes: (ticketAttributes.value || []).map(attr => ({
    id: attr.id,
    name: attr.attributeDisplayName,
    values: attr.attributeValues.map(value => ({
      id: value,
      name: value,
    })),
  })),
}));

const selectedFilters = computed(() => {
  const tags = [];
  const filters = ticketFilters.value;

  if (searchQuery.value.trim()) {
    tags.push({
      type: 'search',
      key: 'search',
      value: searchQuery.value.trim(),
      label: `"${searchQuery.value.trim()}"`,
      color: 'blue',
      icon: '🔍',
    });
  }

  filterListOptions.value.forEach(option => {
    // Skip custom_attributes as they're handled separately
    if (option.key === 'custom_attributes') return;

    const filterValues = filters[option.key] || [];
    const options = filterData.value[option.key] || [];

    filterValues.forEach(value => {
      const optionItem = options.find(opt => opt[option.valueKey] === value);
      if (optionItem) {
        tags.push({
          type: option.key,
          key: option.key,
          value: value,
          label: optionItem[option.labelKey],
          color: option.color,
          icon: option.icon,
        });
      }
    });
  });

  // Handle custom attributes filters
  const customAttributeFilters = filters.custom_attributes || {};
  Object.keys(customAttributeFilters).forEach(attributeKey => {
    const filterValue = customAttributeFilters[attributeKey];
    const attribute = ticketAttributes.value.find(
      attr => attr.attributeKey === attributeKey
    );

    if (attribute && filterValue) {
      let label = attribute.attributeDisplayName;

      // Format label based on filter value structure
      if (Array.isArray(filterValue)) {
        label = `${attribute.attributeDisplayName}: ${filterValue.join(', ')}`;
      } else if (
        typeof filterValue === 'object' &&
        filterValue.operator &&
        filterValue.value !== undefined
      ) {
        const operatorLabels = {
          equals: '=',
          not_equals: '≠',
          greater_than: '>',
          less_than: '<',
          greater_than_or_equal: '≥',
          less_than_or_equal: '≤',
          contains: 'contém',
          not_contains: 'não contém',
          starts_with: 'começa com',
          ends_with: 'termina com',
        };
        const opLabel =
          operatorLabels[filterValue.operator] || filterValue.operator;
        label = `${attribute.attributeDisplayName} ${opLabel} ${filterValue.value}`;
      }

      tags.push({
        type: 'custom_attribute',
        key: 'custom_attributes',
        attributeKey: attributeKey,
        value: filterValue,
        label: label,
        color: 'indigo',
        icon: '🏷️',
      });
    }
  });

  return tags;
});

const loading = computed(() => uiFlags.value.isFetching);
const showBlockingLoading = computed(() => isBootstrapping.value);

const meta = computed(() => {
  const storeMeta = store.getters['tickets/getMeta'];
  return (
    storeMeta || {
      count: 0,
      currentPage: 1,
      totalPages: 1,
      per_page: 25,
    }
  );
});

// Watch search query and save to localStorage
watch(searchQuery, newQuery => {
  try {
    if (newQuery && newQuery.trim()) {
      localStorage.setItem(STORAGE_KEY_SEARCH_QUERY, newQuery);
    } else {
      localStorage.removeItem(STORAGE_KEY_SEARCH_QUERY);
    }
  } catch (e) {
    // Error saving search query to localStorage - silently fail
  }
});

const fetchTickets = async (page = 1) => {
  try {
    error.value = null;
    currentPage.value = page;
    const params = {
      page: page,
      per_page: perPage.value,
    };

    if (searchQuery.value.trim()) {
      params.search = searchQuery.value.trim();
    }

    await store.dispatch('tickets/getTickets', {
      ...params,
      filterView: 'list',
    });
  } catch (err) {
    error.value = err.message || 'Erro ao carregar tickets';
  }
};

const handlePageChange = newPage => {
  fetchTickets(newPage);
};

const refreshTickets = () => {
  fetchTickets(1);
};

const saveFiltersToBackend = (immediate = false) => {
  if (saveFiltersTimeout.value) {
    clearTimeout(saveFiltersTimeout.value);
  }

  const save = async () => {
    await store.dispatch('tickets/saveFiltersToBackend', {
      view: 'list',
      searchQuery: searchQuery.value,
    });
  };

  if (immediate) {
    save();
  } else {
    saveFiltersTimeout.value = setTimeout(save, 1000);
  }
};

const handleSearch = () => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }

  searchTimeout.value = setTimeout(async () => {
    await fetchTickets(1);
    saveFiltersToBackend();
    await nextTick();

    if (searchInputRef.value && searchInputRef.value.$el) {
      const inputElement = searchInputRef.value.$el.querySelector('input');
      if (inputElement) inputElement.focus();
    }
  }, 500);
};

onMounted(async () => {
  try {
    try {
      const savedData = await store.dispatch('tickets/loadFiltersFromBackend', {
        view: 'list',
      });
      if (savedData.filters) {
        if (savedData.searchQuery) {
          searchQuery.value = savedData.searchQuery;
        }
      } else {
        store.dispatch('tickets/clearFilters', { view: 'list' });
        searchQuery.value = '';
      }
    } catch (err) {
      // Error loading saved filters - silently fail
      store.dispatch('tickets/clearFilters', { view: 'list' });
      searchQuery.value = '';
    }

    loadStoredFilterValues();

    await Promise.allSettled([
      fetchTickets(),
      store.dispatch('tickets/getStatuses'),
      store.dispatch('tickets/getPriorities'),
      store.dispatch('teams/get'),
      store.dispatch('agents/get'),
      store.dispatch('contacts/get', { withTickets: true }),
      store.dispatch('attributes/get'),
      store.dispatch('businessAccounts/get'),
    ]);

    await nextTick();
    if (ticketFiltersRef.value?.searchInputRef) {
      searchInputRef.value = ticketFiltersRef.value.searchInputRef;
    }
  } finally {
    isBootstrapping.value = false;
  }
});

onBeforeUnmount(() => {
  if (saveFiltersTimeout.value) {
    clearTimeout(saveFiltersTimeout.value);
  }
  saveFiltersToBackend(true);
});

const isEmpty = computed(() => {
  const hasActiveFilters = selectedFilters.value.length > 0;
  const hasSearchQuery = searchQuery.value.trim().length > 0;
  return (
    !showBlockingLoading.value &&
    !error.value &&
    tickets.value.length === 0 &&
    !hasActiveFilters &&
    !hasSearchQuery
  );
});

const formatDescription = description => {
  if (description.length <= 60) return description;
  return description.substring(0, 60) + '...';
};

const formatCustomAttributeValue = (attribute, value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const attributeType = attribute.attributeDisplayType;

  switch (attributeType) {
    case 'date':
      // Format date to show only date part (no time)
      try {
        const date = new Date(value);
        if (!Number.isNaN(date.getTime())) {
          return date.toLocaleDateString('pt-BR');
        }
      } catch (e) {
        // If parsing fails, return the value as-is
      }
      return value;
    case 'number':
      return Number(value).toLocaleString('pt-BR');
    case 'checkbox':
      return value ? 'Sim' : 'Não';
    case 'list':
      return Array.isArray(value) ? value.join(', ') : value;
    default:
      return value;
  }
};

const handleRowClick = ticketId => {
  router.push({ name: 'helpdesk_ticket_view', params: { id: ticketId } });
};

const handleButtonClick = (event, action, ticketId) => {
  event.stopPropagation();
  if (action === 'delete') {
    // eslint-disable-next-line no-alert, no-restricted-globals
    if (confirm(t('TICKETS.DELETE_CONFIRMATION'))) {
      store
        .dispatch('tickets/delete', ticketId)
        .then(() => {
          // eslint-disable-next-line no-alert
          alert(t('TICKETS.DELETE_SUCCESS'));
          fetchTickets(currentPage.value);
        })
        .catch(err => {
          alert(
            t('TICKETS.DELETE_ERROR', {
              error: err.message || t('TICKETS.UNKNOWN_ERROR'),
            })
          );
        });
    }
  } else if (action === 'view') {
    router.push({ name: 'helpdesk_ticket_view', params: { id: ticketId } });
  }
};

const handleClearFilters = () => {
  store.dispatch('tickets/clearFilters', { view: 'list' });
  searchQuery.value = '';
  // Clear from localStorage
  localStorage.removeItem(STORAGE_KEY_FILTER_VALUES);
  localStorage.removeItem(STORAGE_KEY_SEARCH_QUERY);
  fetchTickets(1);
  saveFiltersToBackend();
};

const getSelectedItems = (filterKey, options) => {
  const filters = ticketFilters.value;
  const selectedValues = filters[filterKey] || [];
  const option = filterListOptions.value.find(o => o.key === filterKey);
  if (!option) return [];

  return options.filter(optionItem => {
    const value = optionItem[option.valueKey];
    return selectedValues.includes(value);
  });
};

const handleItemSelect = (filterKey, item) => {
  const filters = ticketFilters.value;
  const currentValues = filters[filterKey] || [];
  const option = filterListOptions.value.find(o => o.key === filterKey);
  if (!option) return;

  const value = item[option.valueKey];
  const index = currentValues.indexOf(value);

  if (index > -1) {
    currentValues.splice(index, 1);
  } else {
    currentValues.push(value);
  }

  const newFilters = { ...filters };
  if (currentValues.length > 0) {
    newFilters[filterKey] = currentValues;
  } else {
    delete newFilters[filterKey];
  }

  store.dispatch('tickets/setFilters', { view: 'list', filters: newFilters });
  fetchTickets(1); // Reset to first page when filters change
  saveFiltersToBackend();
};

const handleCustomAttributeFilter = async attributeFilterData => {
  const filters = ticketFilters.value;
  const customAttributeFilters = { ...(filters.custom_attributes || {}) };

  if (
    attributeFilterData.value === null ||
    (Array.isArray(attributeFilterData.value) &&
      attributeFilterData.value.length === 0)
  ) {
    delete customAttributeFilters[attributeFilterData.attributeKey];
  } else {
    customAttributeFilters[attributeFilterData.attributeKey] =
      attributeFilterData.value;
  }

  // Create a new filters object, explicitly handling custom_attributes
  const newFilters = { ...filters };

  if (Object.keys(customAttributeFilters).length > 0) {
    newFilters.custom_attributes = customAttributeFilters;
  } else {
    // Explicitly set to empty object so the mutation can clear it
    newFilters.custom_attributes = {};
  }

  store.dispatch('tickets/setFilters', { view: 'list', filters: newFilters });

  // Wait for the store update to complete before fetching
  await nextTick();
  fetchTickets(1);
};

const getCustomAttributeFilterValue = attributeKey => {
  const filters = ticketFilters.value;
  return filters.custom_attributes?.[attributeKey] || null;
};

const removeFilter = filter => {
  // Handle search filter
  if (filter.key === 'search' || filter.type === 'search') {
    searchQuery.value = '';
    fetchTickets(1);
    saveFiltersToBackend();
    return;
  }

  // Handle custom attributes - check if it has attributeKey (which only custom attributes have)
  if (filter.attributeKey) {
    handleCustomAttributeFilter({
      attributeKey: filter.attributeKey,
      value: null,
    });
    return;
  }

  // Handle regular filters (status, priority, etc.)
  const filterKey = filter.key || filter.type;
  const value = filter.value;
  const filters = ticketFilters.value;
  const currentValues = filters[filterKey] || [];
  const index = currentValues.indexOf(value);

  if (index > -1) {
    currentValues.splice(index, 1);

    const newFilters = { ...filters };
    if (currentValues.length > 0) {
      newFilters[filterKey] = currentValues;
    } else {
      delete newFilters[filterKey];
    }

    store.dispatch('tickets/setFilters', {
      view: 'list',
      filters: newFilters,
    });
    fetchTickets(1); // Reset to first page when filters change
    saveFiltersToBackend();
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Header -->
    <div class="flex items-center justify-between p-6 border-b border-n-weak">
      <div>
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('TICKETS.SUPPORT_TICKETS') }}
        </h1>
        <p v-if="meta && meta.count > 0" class="text-n-slate-11 mt-1">
          {{ meta.count }}
          {{ meta.count !== 1 ? t('TICKETS.TICKETS') : t('TICKETS.TICKET') }}
          {{
            meta.count !== 1 ? t('TICKETS.FOUND_PLURAL') : t('TICKETS.FOUND')
          }}
        </p>
      </div>

      <div class="flex items-center space-x-3">
        <ColumnConfiguration
          :columns="allColumnOptions"
          :visible-columns="visibleColumns"
          @update:visible-columns="visibleColumns = $event"
        />

        <FilterConfiguration
          :filters="filterListOptions"
          :visible-filters="visibleFilters"
          @update:visible-filters="visibleFilters = $event"
        />

        <Button
          color="slate"
          size="sm"
          :disabled="loading"
          class="w-40"
          @click="refreshTickets"
        >
          <svg
            class="h-4 w-4 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {{ loading ? t('TICKETS.LOADING') : t('TICKETS.REFRESH') }}
        </Button>

        <Button color="blue" size="sm" @click="$emit('createTicket')">
          <svg
            class="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          {{ t('TICKETS.NEW_TICKET') }}
        </Button>
      </div>
    </div>

    <TicketFiltersBar
      ref="ticketFiltersRef"
      :loading="loading"
      :filter-list-options="filterListOptions"
      :filter-data="filterData"
      :visible-filters="visibleFilters"
      :ticket-attributes="ticketAttributes"
      :search-query="searchQuery"
      :selected-filters="selectedFilters"
      :get-selected-items="getSelectedItems"
      :handle-item-select="handleItemSelect"
      :handle-custom-attribute-filter="handleCustomAttributeFilter"
      :get-custom-attribute-filter-value="getCustomAttributeFilterValue"
      :remove-filter="removeFilter"
      :handle-clear-filters="handleClearFilters"
      :handle-search="handleSearch"
      :search-placeholder="t('TICKETS.SEARCH_PLACEHOLDER')"
      filters-container-class="px-6 py-4 border-n-weak"
      selected-filters-container-class="px-6 py-4 border-b border-n-weak"
      @update:search-query="value => (searchQuery = value)"
    />

    <!-- Loading State -->
    <div
      v-if="showBlockingLoading"
      class="flex-1 flex items-center justify-center"
    >
      <div class="text-center">
        <div
          class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-n-solid-4 mb-4"
        >
          <svg
            class="h-6 w-6 text-n-slate-10 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
        <h3 class="text-sm font-medium text-n-slate-11 mb-2">
          {{ t('TICKETS.LOADING_TICKETS') }}
        </h3>
        <p class="text-sm text-n-slate-10">
          {{ t('TICKETS.WAIT_FOR_DATA') }}
        </p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="flex-1 flex items-center justify-center">
      <div class="text-center">
        <div
          class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4"
        >
          <svg
            class="h-6 w-6 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h3 class="text-sm font-medium text-red-800 mb-2">
          {{ t('TICKETS.ERROR_LOADING_TICKETS') }}
        </h3>
        <p class="text-sm text-red-600 mb-4">{{ error }}</p>
        <Button color="ruby" size="sm" @click="fetchTickets">
          {{ t('TICKETS.TRY_AGAIN') }}
        </Button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="isEmpty" class="flex-1 flex items-center justify-center">
      <div class="text-center">
        <div
          class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-n-solid-4 mb-6"
        >
          <svg
            class="h-8 w-8 text-n-slate-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-n-slate-11 mb-3">
          {{ t('TICKETS.NO_TICKETS_FOUND') }}
        </h3>
        <p class="text-sm text-n-slate-10 mb-6">
          {{ t('TICKETS.START_CREATING_FIRST_TICKET') }}
        </p>
        <Button color="blue" size="lg" @click="$emit('createTicket')">
          <svg
            class="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          {{ t('TICKETS.CREATE_FIRST_TICKET') }}
        </Button>
      </div>
    </div>

    <!-- Table -->
    <div
      v-show="!showBlockingLoading && !error && !isEmpty"
      class="flex-1 overflow-auto"
    >
      <div class="px-6 py-4">
        <div
          class="bg-n-solid-3 rounded-lg border border-n-weak overflow-hidden"
        >
          <div class="overflow-x-auto hidden md:block">
            <table class="w-full">
              <thead class="bg-n-solid-4 border-b border-n-weak">
                <tr>
                  <th
                    v-if="visibleColumns.includes('title')"
                    class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider w-48"
                  >
                    {{ t('TICKETS.TITLE') }}
                  </th>
                  <th
                    v-if="visibleColumns.includes('description')"
                    class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider w-64"
                  >
                    {{ t('TICKETS.DESCRIPTION') }}
                  </th>
                  <th
                    v-if="visibleColumns.includes('status')"
                    class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider w-48"
                  >
                    {{ t('TICKETS.STATUS') }}
                  </th>
                  <th
                    v-if="visibleColumns.includes('priority')"
                    class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider w-48"
                  >
                    {{ t('TICKETS.PRIORITY') }}
                  </th>
                  <th
                    v-if="visibleColumns.includes('contact')"
                    class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider w-48"
                  >
                    {{ t('TICKETS.CONTACT') }}
                  </th>
                  <th
                    v-if="visibleColumns.includes('team')"
                    class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider w-48"
                  >
                    {{ t('TICKETS.TEAM') }}
                  </th>
                  <th
                    v-if="visibleColumns.includes('assignee')"
                    class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider w-48"
                  >
                    {{ t('TICKETS.ASSIGNEE') }}
                  </th>
                  <th
                    v-if="visibleColumns.includes('business_account')"
                    class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider w-48"
                  >
                    {{ t('TICKETS.BUSINESS_ACCOUNT') }}
                  </th>
                  <!-- Custom Attribute Column Headers -->
                  <th
                    v-for="attribute in visibleCustomAttributeColumns"
                    :key="attribute.id"
                    class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider w-48"
                  >
                    {{ attribute.attributeDisplayName }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-n-solid-3 divide-y divide-n-weak">
                <!-- Regular ticket rows -->
                <tr
                  v-for="ticket in tickets"
                  :key="ticket.id"
                  class="hover:bg-n-solid-4 transition-colors cursor-pointer"
                  @click="handleRowClick(ticket.id)"
                >
                  <td
                    v-if="visibleColumns.includes('title')"
                    class="px-6 py-4 whitespace-nowrap"
                  >
                    <div class="text-sm font-medium text-n-slate-12">
                      {{ ticket.title }}
                    </div>
                  </td>

                  <td
                    v-if="visibleColumns.includes('description')"
                    class="px-6 py-4"
                  >
                    <div
                      class="text-sm text-n-slate-11 max-w-xs truncate"
                      :title="ticket.description"
                    >
                      {{ formatDescription(ticket.description) }}
                    </div>
                  </td>

                  <td
                    v-if="visibleColumns.includes('status')"
                    class="px-6 py-4 whitespace-nowrap"
                  >
                    <span
                      class="inline-flex px-2 py-1 text-xs font-medium rounded-full"
                    >
                      {{ ticket.status_name }}
                    </span>
                  </td>

                  <td
                    v-if="visibleColumns.includes('priority')"
                    class="px-6 py-4 whitespace-nowrap"
                  >
                    <span
                      class="inline-flex px-2 py-1 text-xs font-medium rounded-full"
                    >
                      {{ ticket.priority_name }}
                    </span>
                  </td>

                  <td
                    v-if="visibleColumns.includes('contact')"
                    class="px-6 py-4 whitespace-nowrap"
                  >
                    <div class="text-sm text-n-slate-11">
                      {{ ticket.contact_name || '-' }}
                    </div>
                  </td>

                  <td
                    v-if="visibleColumns.includes('team')"
                    class="px-6 py-4 whitespace-nowrap"
                  >
                    <div class="text-sm text-n-slate-11">
                      {{ ticket.team_name || t('TICKETS.TEAM_NOT_DEFINED') }}
                    </div>
                  </td>

                  <td
                    v-if="visibleColumns.includes('assignee')"
                    class="px-6 py-4 whitespace-nowrap"
                  >
                    <div class="flex items-center">
                      <div>
                        <div class="text-sm font-medium text-n-slate-12">
                          {{
                            ticket.assignee_name || t('TICKETS.USER_NOT_FOUND')
                          }}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td
                    v-if="visibleColumns.includes('business_account')"
                    class="px-6 py-4 whitespace-nowrap"
                  >
                    <div class="flex items-center">
                      <div>
                        <div class="text-sm font-medium text-n-slate-12">
                          {{
                            ticket.business_account_name ||
                            t('TICKETS.BUSINESS_ACCOUNT_NOT_FOUND')
                          }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <!-- Custom Attribute Columns -->
                  <td
                    v-for="attribute in visibleCustomAttributeColumns"
                    :key="attribute.id"
                    class="px-6 py-4 whitespace-nowrap"
                  >
                    <div class="text-sm text-n-slate-11">
                      {{
                        formatCustomAttributeValue(
                          attribute,
                          ticket.custom_attributes?.[attribute.attributeKey]
                        )
                      }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- No results message when no tickets -->
            <div v-if="tickets.length === 0" class="text-center py-12">
              <div class="flex flex-col items-center">
                <div
                  class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-n-solid-4 mb-4"
                >
                  <svg
                    class="h-6 w-6 text-n-slate-10"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 class="text-sm font-medium text-n-slate-11 mb-2">
                  {{ t('TICKETS.NO_TICKETS_FOUND') }}
                </h3>
                <p class="text-sm text-n-slate-10 mb-4">
                  {{ t('TICKETS.NO_TICKETS_MATCH_FILTERS') }}
                </p>
                <Button color="ruby" size="sm" @click="handleClearFilters">
                  <svg
                    class="h-4 w-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  {{ t('TICKETS.CLEAR_FILTERS') }}
                </Button>
              </div>
            </div>
          </div>

          <!-- Mobile Cards View -->
          <div v-if="tickets.length > 0" class="md:hidden p-4 space-y-4">
            <!-- Regular ticket cards -->
            <div
              v-for="ticket in tickets"
              :key="ticket.id"
              class="bg-n-solid-3 rounded-lg border border-n-weak p-4 cursor-pointer hover:bg-n-solid-4 transition-colors"
              @click="handleRowClick(ticket.id)"
            >
              <div class="flex items-start justify-between mb-3">
                <h3
                  class="text-sm font-medium text-n-slate-12 truncate flex-1 mr-2"
                >
                  {{ ticket.title }}
                </h3>
                <span
                  class="inline-flex px-2 py-1 text-xs font-medium rounded-full flex-shrink-0"
                >
                  {{ ticket.status_name }}
                </span>
              </div>

              <p class="text-sm text-n-slate-11 mb-3 line-clamp-2">
                {{ ticket.description }}
              </p>

              <div
                class="flex items-center justify-between text-xs text-n-slate-10"
              >
                <div class="flex items-center space-x-4">
                  <span>{{ ticket.contact_name || '-' }}</span>
                  <span>{{ ticket.team_name || 'Time não definido' }}</span>
                </div>
                <div class="flex items-center space-x-2">
                  <Button
                    variant="link"
                    color="blue"
                    size="sm"
                    @click="handleButtonClick($event, 'view', ticket.id)"
                  >
                    {{ t('TICKETS.VIEW') }}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div
            v-if="meta && meta.count > 0"
            class="px-6 py-4 border-t border-n-weak"
          >
            <div class="flex items-center justify-between">
              <div class="text-sm text-n-slate-11">
                {{ t('TICKETS.SHOWING') }}
                {{ (currentPage - 1) * perPage + 1 }}
                {{ t('TICKETS.TO') }}
                {{ Math.min(currentPage * perPage, meta.count) }}
                {{ t('TICKETS.OF') }} {{ meta.count }}
                {{ t('TICKETS.TICKETS') }}
              </div>
              <div class="flex items-center space-x-2">
                <Button
                  variant="outline"
                  color="slate"
                  size="sm"
                  :disabled="currentPage === 1"
                  @click="handlePageChange(currentPage - 1)"
                >
                  <svg
                    class="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  {{ t('TICKETS.PREVIOUS') }}
                </Button>

                <span class="text-sm text-n-slate-11 px-3">
                  {{ t('TICKETS.PAGE') }} {{ currentPage }}
                  {{ t('TICKETS.OF') }} {{ meta.total_pages || 1 }}
                </span>

                <Button
                  variant="outline"
                  color="slate"
                  size="sm"
                  :disabled="currentPage >= (meta.total_pages || 1)"
                  @click="handlePageChange(currentPage + 1)"
                >
                  {{ t('TICKETS.NEXT') }}
                  <svg
                    class="h-4 w-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
